import React, { useEffect, useRef, useState } from "react";
import "../Styles/PhotographUploader.css";
import { getUploadValidationError, UPLOAD_LIMITS } from "../utils/uploadLimits";

const PhotographUploader = ({
  profileImages,
  setProfileImages,
  accessOnRequest,
  setAccessOnRequest,
  mainIndex,
  setMainIndex,
  otherMediaFiles = [],
  additionalPhotoCount = 0,
}) => {
  console.log(profileImages);

  // Rehydrate from the parent's File list so navigating back to this step
  // (which unmounts/remounts this component) doesn't lose already-selected images.
  const [images, setImages] = useState(() =>
    (profileImages || []).map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    })),
  ); // Stores { file, preview }
  const [currentIndex, setCurrentIndex] = useState(0);
  const fileInputRef = useRef();

  const handleFiles = (files) => {
    const imageFiles = Array.from(files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (imageFiles.length !== files.length) {
      alert("Please select image files only.");
      return;
    }

    const validationError = getUploadValidationError({
      photoFiles: [...images.map((image) => image.file), ...imageFiles],
      photoCount: images.length + additionalPhotoCount + imageFiles.length,
      otherFiles: otherMediaFiles,
    });
    if (validationError) {
      alert(validationError);
      return;
    }

    const readers = imageFiles.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) =>
          resolve({
            file,
            preview: e.target.result, // For display
          });
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then((newImages) => {
      setImages((prev) => [...prev, ...newImages]);
      if (images.length === 0) setCurrentIndex(0);
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const onUploadClick = () => fileInputRef.current.click();

  const removeImage = (index) => {
    const updated = images.filter((_, i) => i !== index);
    setImages(updated);
    if (index === currentIndex && currentIndex > 0)
      setCurrentIndex(currentIndex - 1);
  };

  // Keep parent state in sync with File objects
  useEffect(() => {
    setProfileImages(images.map((img) => img.file));
  }, [images, setProfileImages]);

  const handleSetMain = () => {
    setMainIndex(currentIndex);
  };

  return (
    <div className="PhotographUploader-container">
      <h2 className="PhotographUploader-title">Photographs</h2>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
        hidden
      />

      {!images.length ? (
        <div
          className="PhotographUploader-drop-area"
          onDrop={onDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={onUploadClick}
        >
          <div className="PhotographUploader-upload-icon">
            <img src={require("../assests/images/upload-image.png")} alt="" />
          </div>
          <p className="PhotographUploader-upload-text">Upload / Drag images</p>
          <p className="PhotographUploader-subtext">
            Recommended image size: 500 × 700 px. Up to 10 photos, 3 MB each.
          </p>
        </div>
      ) : (
        <>
          {/* Image viewer */}
          <div className="PhotographUploader-gallery">
            <div className="PhotographUploader-main-view">
              <div className="PhotographUploader-main-view2">
                <button
                  className="PhotographUploader-nav-button"
                  onClick={() =>
                    setCurrentIndex((prev) => Math.max(prev - 1, 0))
                  }
                  disabled={currentIndex === 0}
                >
                  ‹
                </button>
                <img
                  src={images[currentIndex].preview}
                  alt="Main"
                  className="PhotographUploader-main-image"
                />
                <button
                  className="PhotographUploader-nav-button"
                  onClick={() =>
                    setCurrentIndex((prev) =>
                      Math.min(prev + 1, images.length - 1),
                    )
                  }
                  disabled={currentIndex === images.length - 1}
                >
                  ›
                </button>
              </div>
            </div>

            <div className="profile-bottom-bar">
              <div className="profile-dots">
                {images.map((_, i) => (
                  <span
                    key={i}
                    className={`dot ${i === currentIndex ? "active" : ""}`}
                  />
                ))}
              </div>

              <div className="d-flex gap-3">
                <input
                  type="checkbox"
                  checked={mainIndex === currentIndex}
                  onChange={handleSetMain}
                />
                <span className="set-main-text">Set as main profile</span>
                <span className="profile-count">
                  {currentIndex + 1}/{images.length}
                </span>
              </div>
            </div>

            {/* Thumbnails */}
            <div className="PhotographUploader-thumbnails">
              {images.map((img, index) => (
                <div
                  key={index}
                  className="PhotographUploader-thumbnail-wrapper"
                >
                  <img
                    src={img.preview}
                    alt={`Thumbnail ${index}`}
                    className={`PhotographUploader-thumbnail ${
                      index === currentIndex ? "active" : ""
                    }`}
                    onClick={() => setCurrentIndex(index)}
                  />
                  <button
                    className="PhotographUploader-remove-button"
                    onClick={() => removeImage(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
              <div
                className="PhotographUploader-add-thumbnail"
                onClick={onUploadClick}
                title={`Add photos (up to ${UPLOAD_LIMITS.photoCount}, 3 MB each)`}
              >
                ＋
              </div>
            </div>
          </div>
        </>
      )}

      {/* Note */}
      {/* <p className="PhotographUploader-note">
        <span className="PhotographUploader-note-icon"></span>
        <strong>Private images</strong> – Can be accessed only after your
        approval
      </p> */}
      <p
        className="PhotographUploader-note d-flex align-items-center gap-2"
        style={{ cursor: "pointer" }}
      >
        <input
          type="checkbox"
          id="private-images-note"
          checked={!accessOnRequest} // 👈 inverted
          onChange={(e) => setAccessOnRequest(!e.target.checked)}
        />

        {/* <span className="PhotographUploader-note-icon"></span> */}

        <label htmlFor="private-images-note" style={{ cursor: "pointer" }}>
          <strong>Private images</strong> – Can be accessed only after your
          approval
        </label>
      </p>
    </div>
  );
};

export default PhotographUploader;
